import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { AlertTriangle, Database, Shield } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSystem = () => {
  const { user } = useAuth();
  const [confirmText, setConfirmText] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const isSuperAdmin = user?.role === 'super_admin';

  const runWipe = async () => {
    if (confirmText !== 'WIPE') {
      toast.error('Type WIPE to confirm');
      return;
    }
    if (!window.confirm('This permanently deletes ALL operational data (invoices, schedule, customers, employees, vips, photos, etc.). Only super_admin accounts and the role/audit framework will remain. Continue?')) {
      return;
    }
    setRunning(true);
    setResult(null);
    try {
      const { data } = await axios.post(
        `${API}/admin/system/wipe-operational-data?confirm=WIPE`,
        {},
        { withCredentials: true },
      );
      setResult(data);
      toast.success('Operational wipe complete');
      setConfirmText('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Wipe failed');
    }
    setRunning(false);
  };

  return (
    <div data-testid="admin-system-page" className="space-y-8 max-w-3xl">
      <div>
        <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-2 flex items-center gap-2"><Shield className="w-6 h-6 text-[#D4AF37]" /> System</h2>
        <p className="text-[#A3A3A3]">Dangerous operations — only super admins can perform these.</p>
      </div>

      {!isSuperAdmin ? (
        <div data-testid="not-super-admin" className="bg-[#141414] border border-[#262626] rounded-md p-6 text-[#A3A3A3]">
          <p className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-500" /> Only super administrators can access these tools.</p>
        </div>
      ) : (
        <>
          <div className="bg-[#141414] border border-red-900/50 rounded-md p-6 space-y-4">
            <div className="flex items-start gap-3">
              <Database className="w-6 h-6 text-red-500 shrink-0 mt-0.5" />
              <div>
                <p className="text-white font-medium">Wipe All Operational Data</p>
                <p className="text-[#A3A3A3] text-sm mt-1">
                  Permanently deletes every operational record so you can onboard real users on a clean slate.
                </p>
                <ul className="mt-3 text-[#A3A3A3] text-sm list-disc pl-5 space-y-1">
                  <li><span className="text-red-400">Will be deleted:</span> invoices, estimates, appointments, schedules, memberships, VIP referrals/payouts, timesheets, bonuses, time-off, vehicles, on-call, customers, employees (non-super-admin users), inventory, service requests, photos, notifications.</li>
                  <li><span className="text-green-400">Preserved:</span> super_admin accounts, role/permission matrix, reminder settings, audit log (history).</li>
                </ul>
              </div>
            </div>

            <div className="space-y-2 pt-3 border-t border-[#262626]">
              <Label className="text-white">Type <span className="text-red-400 font-mono">WIPE</span> to confirm</Label>
              <Input
                data-testid="wipe-confirm-input"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="WIPE"
                className="bg-[#0A0A0A] border-[#262626] text-white font-mono uppercase"
              />
              <Button
                data-testid="wipe-execute-btn"
                onClick={runWipe}
                disabled={running || confirmText !== 'WIPE'}
                className="bg-red-600 text-white hover:bg-red-700 disabled:opacity-40"
              >
                {running ? 'Wiping…' : 'Permanently Wipe Operational Data'}
              </Button>
            </div>
          </div>

          {result && (
            <div data-testid="wipe-result" className="bg-[#141414] border border-[#262626] rounded-md p-5">
              <p className="text-white font-medium mb-2">Wipe complete</p>
              <div className="text-sm">
                <p className="text-[#A3A3A3] mb-1">Deleted:</p>
                <ul className="text-[#A3A3A3] pl-5 list-disc">
                  {Object.entries(result.wiped || {}).map(([k, v]) => (
                    <li key={k}><span className="text-white">{k}</span> · {String(v)}</li>
                  ))}
                  {Object.keys(result.wiped || {}).length === 0 && <li>Nothing to delete (already clean)</li>}
                </ul>
                <p className="text-[#A3A3A3] mt-3">Preserved: {(result.preserved || []).join(', ')}</p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminSystem;
